import { PRD3030_R01Dto, PRD3030_R01SubtotalData, PRD3030_R01DateSubtotalData, PRD3030_R01ItemSubtotalRow, PRD3030_R01DateSubtotalRow, PRD3030_R01GridRowData, PRD3030_R01_SUBTOTAL_LEVELS, PRD3030_R01_SUBTOTAL_TYPES } from '../Dto_PRD3030_R01';
import { ExtendedPRD3030_R01Dto } from '../Store_PRD3030_R01';
import { mergeDataWithSubtotalsGeneric } from 'src/utils/subtotal/merge';
import { createSubtotalGuards } from 'src/utils/subtotal/guards';
import { formatDate, toLocalDateTime } from 'src/utils/string/stringUtil';

const { isSubtotalRow } = createSubtotalGuards<PRD3030_R01GridRowData>({
    isSubtotalKey: 'isSubtotal',
    typeKey: 'subtotalType',
    itemTypeValue: PRD3030_R01_SUBTOTAL_TYPES.ITEM,
    dateTypeValue: PRD3030_R01_SUBTOTAL_TYPES.DATE,
});

/**
 * 안전한 Date 변환 헬퍼
 * - Date, string, number 등 다양한 입력을 Date로 정규화
 * - 유효하지 않으면 undefined 반환
 */
const ensureDate = (value: any): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
};

/**
 * 품목별 소계 데이터 계산
 */
export const calculateSubtotals = (data: ExtendedPRD3030_R01Dto[]): PRD3030_R01SubtotalData[] => {
    if (!data || data.length === 0) return [];
    const grouped = data.reduce((acc, item) => {
        const key = item.itemCd;
        if (!acc[key]) {
            acc[key] = {
                itemCd: item.itemCd,
                itemSpec: item.itemSpec || '',
                itemUnit: item.itemUnit || '',
                count: 0,
                orderQty: 0,
                productQty: 0,
                badQty: 0,
                acceptQty: 0
            };
        }
        acc[key].count += 1;
        acc[key].orderQty += Number(item.orderQty) || 0;
        acc[key].productQty += Number(item.productQty) || 0;
        acc[key].badQty += Number(item.badQty) || 0;
        acc[key].acceptQty += Number(item.acceptQty) || 0;
        return acc;
    }, {} as Record<string, PRD3030_R01SubtotalData>);
    return Object.values(grouped).sort((a, b) => a.itemCd.localeCompare(b.itemCd));
};

/**
 * 생산일자별 소계 데이터 계산
 */
export const calculateDateSubtotals = (data: ExtendedPRD3030_R01Dto[]): PRD3030_R01DateSubtotalData[] => {
    if (!data || data.length === 0) return [];
    const grouped = data.reduce((acc, item) => {
        const dateObj = ensureDate(item.productDt);
        const dateKey = dateObj ? dateObj.toISOString() : '';
        const key = `${item.itemCd}_${dateKey}`;
        if (!acc[key]) {
            acc[key] = {
                itemCd: item.itemCd,
                itemSpec: item.itemSpec || '',
                itemUnit: item.itemUnit || '',
                productDt: dateObj ? formatDate(dateObj) : formatDate(new Date()),
                count: 0,
                dateCount: 0,
                orderQty: 0,
                productQty: 0,
                badQty: 0,
                acceptQty: 0
            } as PRD3030_R01DateSubtotalData;
        }
        acc[key].count += 1;
        acc[key].dateCount += 1;
        acc[key].orderQty += Number(item.orderQty) || 0;
        acc[key].productQty += Number(item.productQty) || 0;
        acc[key].badQty += Number(item.badQty) || 0;
        acc[key].acceptQty += Number(item.acceptQty) || 0;
        return acc;
    }, {} as Record<string, PRD3030_R01DateSubtotalData>);
    
    return Object.values(grouped).sort((a, b) => {
        if (a.itemCd === b.itemCd) {
            const aTime = ensureDate(a.productDt)?.getTime() || 0;
            const bTime = ensureDate(b.productDt)?.getTime() || 0;
            return aTime - bTime;
        }
        return a.itemCd.localeCompare(b.itemCd);
    });
};

/**
 * 품목별 소계 행 데이터 생성
 */
export const createSubtotalRow = (subtotal: PRD3030_R01SubtotalData): PRD3030_R01ItemSubtotalRow => ({
    valueIndex: 0,
    orderIndex: 0,
    orderIndexSeq: 0,
    productResultNo: `ITEM_SUBTOTAL_${subtotal.itemCd}`,
    compCd: '',
    compNm: '',
    productDt: formatDate(new Date()),
    orderType: '',
    orderTypeNm: '',
    workplaceCd: '',
    workplaceNm: '',
    jobTeam: '',
    jobTeamNm: '',
    itemCd: subtotal.itemCd,
    itemStatus: '',
    itemNm: '',
    itemSpec: subtotal.itemSpec,
    itemUnit: subtotal.itemUnit,
    productOrderNo: '',
    orderQty: subtotal.orderQty,
    productEmp: '',
    productEmpNm: '',
    workStartDt: toLocalDateTime(new Date()),
    workEndDt: undefined,
    empNm: '',
    productQty: subtotal.productQty,
    badQty: subtotal.badQty,
    acceptQty: subtotal.acceptQty,
    brandCd: '',
    brandNm: '',
    logicCenter: '',
    warehouseCd: '',
    warehouseNm: '',
	basOrderType: '',
    basOrderNo: '',
    basOrderItemNo: '',
    kpiYn: '',
    inspectQty: 0,
    inspectYn: 'N',

    inserted: false,
    isModified: false,
    startDate: undefined,
    endDate: undefined,
    insertUser: undefined,
    insertForm: undefined,
    insertDt: undefined,
    updateUser: undefined,
    updateForm: undefined,
    updateDt: undefined,
    codeEntity: null,
    remark: undefined,
    count: subtotal.count,
    isSubtotal: true,
    subtotalType: PRD3030_R01_SUBTOTAL_TYPES.ITEM,
    level: PRD3030_R01_SUBTOTAL_LEVELS.ITEM,
});

/**
 * 생산일자별 소계 행 데이터 생성
 */
export const createDateSubtotalRow = (subtotal: PRD3030_R01DateSubtotalData): PRD3030_R01DateSubtotalRow => ({
    valueIndex: 0,
    orderIndex: 0,
    orderIndexSeq: 0,
    productResultNo: `DATE_SUBTOTAL_${subtotal.itemCd}_${subtotal.productDt}`,
    compCd: '',
    compNm: '',
    productDt: subtotal.productDt || formatDate(new Date()),
    orderType: '',
    orderTypeNm: '',
    workplaceCd: '',
    workplaceNm: '',
    jobTeam: '',
    jobTeamNm: '',
    itemCd: subtotal.itemCd,
    itemStatus: '',
    itemNm: '',
    itemSpec: subtotal.itemSpec,
    itemUnit: subtotal.itemUnit,
    productOrderNo: '',
    orderQty: subtotal.orderQty,
    productEmp: '',
    productEmpNm: '',
    workStartDt: toLocalDateTime(new Date()),
    workEndDt: undefined,
    empNm: '',
    productQty: subtotal.productQty,
    badQty: subtotal.badQty,
    acceptQty: subtotal.acceptQty,
    brandCd: '',
    brandNm: '',
    logicCenter: '',
    warehouseCd: '',
    warehouseNm: '',
	basOrderType: '',
    basOrderNo: '',
    basOrderItemNo: '',
    kpiYn: '',
    inspectQty: 0,
    inspectYn: 'N',
	
    inserted: false,
    isModified: false,
    startDate: undefined,
    endDate: undefined,
    insertUser: undefined,
    insertForm: undefined,
    insertDt: undefined,
    updateUser: undefined,
    updateForm: undefined,
    updateDt: undefined,
    codeEntity: null,
    remark: undefined,
    count: subtotal.count,
    isSubtotal: true,
    subtotalType: PRD3030_R01_SUBTOTAL_TYPES.DATE,
    level: PRD3030_R01_SUBTOTAL_LEVELS.DATE,
    dateCount: subtotal.dateCount,
});

/** 상세 행 생성 */
export const createDetailRow = (item: ExtendedPRD3030_R01Dto): PRD3030_R01GridRowData => ({
    ...item,
    isSubtotal: false,
    level: PRD3030_R01_SUBTOTAL_LEVELS.DETAIL,
});

/** 공통 머지 유틸을 사용한 병합 */
export const mergeDataWithSubtotals = (
    original: ExtendedPRD3030_R01Dto[],
    subtotals: PRD3030_R01SubtotalData[],
    dateSubtotals: PRD3030_R01DateSubtotalData[],
    showSubtotals: boolean
): PRD3030_R01GridRowData[] => {
    const itemSubtotalMap = new Map<string, PRD3030_R01SubtotalData>(subtotals.map(s => [s.itemCd, s]));
    const dateSubtotalMap = new Map<string, PRD3030_R01DateSubtotalData>(
        dateSubtotals.map(s => {
            const d = ensureDate(s.productDt);
            const key = `${s.itemCd}|${d ? d.toISOString() : ''}`;
            return [key, s];
        })
    );

    return mergeDataWithSubtotalsGeneric<ExtendedPRD3030_R01Dto, PRD3030_R01GridRowData, PRD3030_R01SubtotalData, PRD3030_R01DateSubtotalData>(
        original,
        {
            getItemKey: (d) => d.itemCd,
            getDateKey: (d) => {
                const dd = ensureDate(d.productDt);
                return dd ? dd.toISOString() : '';
            },
            itemSubtotalMap,
            dateSubtotalMap,
            createDetailRow,
            createItemSubtotalRow: createSubtotalRow,
            createDateSubtotalRow,
            showSubtotals,
            sortDates: (a, b) => a.localeCompare(b),
        }
    );
};

/**
 * 특정 품목의 모든 상세 행 ID 목록 반환 (소계/표시 유틸 계층에 위치)
 */
export const getDetailRowIdsByItem = (itemCd: string, gridData: PRD3030_R01GridRowData[]): string[] => {
    return gridData
        .filter(row => !isSubtotalRow(row) && row.itemCd === itemCd)
        .map(row => row.productResultNo);
};
